import { DayOfWeek, Schedule } from './chores';

/**
 * Google Tasks import — types and schedule inference.
 *
 * The public Google Tasks API (tasks/v1) exposes no recurrence field: recurrence
 * was added to the Tasks *service* in 2019 but never to the third-party API, and
 * Takeout omits it too. Google's own clients read it from an internal endpoint we
 * cannot use.
 *
 * So we reconstruct it. Google materialises each occurrence of a recurring task as
 * a separate task record sharing the same title, which means the gaps between those
 * occurrences carry the recurrence rule implicitly. Everything below is that
 * reconstruction: group by title, diff the dates, and map the typical gap onto a
 * ChoresChamp `Schedule`.
 *
 * The result is a *guess*. Every candidate carries a confidence and a plain-language
 * rationale so the import UI can present it for review rather than apply it blindly.
 */

/** A task list as returned by tasklists.list. */
export interface GoogleTaskList {
  id: string;
  title: string;
}

/** The subset of the tasks/v1 Task resource we actually read. */
export interface GoogleTask {
  id: string;
  title: string;
  notes?: string;
  /** RFC 3339, but date-only in practice — Google discards the time portion. */
  due?: string;
  completed?: string;
  status: 'needsAction' | 'completed';
  updated?: string;
  parent?: string;
  hidden?: boolean;
  deleted?: boolean;
  webViewLink?: string;
}

export interface TaskListWithTasks {
  list: GoogleTaskList;
  tasks: GoogleTask[];
  /** True when the fetch hit its page cap, so this list holds more than was read. */
  truncated?: boolean;
}

export type InferenceConfidence = 'high' | 'medium' | 'low' | 'none';

export interface ScheduleInference {
  /** Best-guess schedule. Falls back to a one-off when there is nothing to infer from. */
  schedule: Schedule;
  confidence: InferenceConfidence;
  /** Typical gap between occurrences, in days. Null when fewer than two dated occurrences. */
  medianGapDays: number | null;
  /** Every observed gap, ascending by occurrence date — shown in the UI to explain the guess. */
  gapsInDays: number[];
  rationale: string;
}

export interface ImportCandidate {
  /** Stable identity for React keys and selection: list id + normalised title. */
  key: string;
  listId: string;
  listTitle: string;
  title: string;
  notes?: string;
  /** Occurrence dates used for inference, ascending. */
  occurrenceDates: Date[];
  /** Total task records in the group, including undated ones. */
  occurrenceCount: number;
  /** The still-open instance, if any — the one an import would carry over as "next due". */
  openTaskId?: string;
  hasOpenInstance: boolean;
  inference: ScheduleInference;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const MINUTES_PER_DAY = 1440;
/** Mean Gregorian month, used to test whether a gap lands on a whole number of months. */
const AVG_MONTH_DAYS = 30.44;
/**
 * How many recent occurrences inform the estimate.
 *
 * More than this adds nothing to a median but does actively harm it: a chore whose
 * cadence changed a year ago would otherwise be averaged across both rhythms.
 */
const MAX_INFERENCE_OCCURRENCES = 8;

const WEEKDAYS: DayOfWeek[] = [
  'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday',
];

/**
 * Reads the calendar date out of an RFC 3339 timestamp as a *local* midnight Date.
 *
 * Google stores due dates as UTC midnight, so `new Date(due)` in any timezone behind
 * UTC reports the previous day. Taking the date prefix avoids that. Completion
 * timestamps are real instants, so this can be a day off for late-evening completions
 * — immaterial at the tolerances the inference works with.
 */
export function calendarDateFromRfc3339(value: string): Date | null {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return Number.isNaN(date.getTime()) ? null : date;
}

/** `YYYY-MM-DD` in local time — the format a `once` schedule's dueDate expects. */
export function toDateKey(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Groups occurrences of the same recurring task, which share a title verbatim. */
export function normalizeTitle(title: string): string {
  return title.trim().toLowerCase().replace(/\s+/g, ' ');
}

/** The date an occurrence happened on: its due date, or failing that when it was completed. */
export function occurrenceDate(task: GoogleTask): Date | null {
  const source = task.due ?? task.completed;
  return source ? calendarDateFromRfc3339(source) : null;
}

function daysBetween(earlier: Date, later: Date): number {
  return Math.round((later.getTime() - earlier.getTime()) / DAY_MS);
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

/** Ties resolve to the first-seen value, since Map preserves insertion order. */
function mostCommon<T extends string | number>(values: T[]): T {
  const counts = new Map<T, number>();
  for (const value of values) {
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  let best = values[0];
  let bestCount = 0;
  for (const [value, count] of counts) {
    if (count > bestCount) {
      best = value;
      bestCount = count;
    }
  }
  return best;
}

/**
 * How much to trust the median gap.
 *
 * A skipped occurrence doubles one gap, which is exactly why the central estimate is
 * a median — but it still widens the spread, correctly dropping confidence.
 */
function scoreConfidence(gaps: number[], medianGap: number): InferenceConfidence {
  if (gaps.length === 1) return 'low';
  const spread = (Math.max(...gaps) - Math.min(...gaps)) / medianGap;
  if (gaps.length >= 3 && spread <= 0.15) return 'high';
  if (spread <= 0.35) return 'medium';
  return 'low';
}

/**
 * Maps a typical gap onto a Schedule, largest unit first.
 *
 * Weekly is tested before monthly deliberately: a tight multiple of 7 (28 days) is
 * far better evidence of a 4-week rhythm than of a monthly one, while a 30- or
 * 31-day gap misses the weekly tolerance and falls through to monthly as it should.
 */
function scheduleForGap(
  gapDays: number,
  dates: Date[],
): { schedule: Schedule; description: string } {
  const last = dates[dates.length - 1];

  if (Math.abs(gapDays - 365) <= 20) {
    return {
      schedule: { type: 'yearly', month: last.getMonth() + 1, dayOfMonth: last.getDate() },
      description: 'about a year apart',
    };
  }

  const weeks = Math.round(gapDays / 7);
  if (weeks >= 1 && weeks <= 8 && Math.abs(gapDays - weeks * 7) <= 1) {
    return {
      schedule: {
        type: 'weekly',
        days: [mostCommon(dates.map((date) => WEEKDAYS[date.getDay()]))],
        interval: weeks,
      },
      description: weeks === 1 ? 'about a week apart' : `about ${weeks} weeks apart`,
    };
  }

  const months = Math.round(gapDays / AVG_MONTH_DAYS);
  if (months >= 1 && months <= 12 && Math.abs(gapDays - months * AVG_MONTH_DAYS) <= Math.max(3, months * 2)) {
    return {
      schedule: {
        type: 'monthly',
        dayOfMonth: mostCommon(dates.map((date) => date.getDate())),
        interval: months,
      },
      description: months === 1 ? 'about a month apart' : `about ${months} months apart`,
    };
  }

  const days = Math.max(1, Math.round(gapDays));
  return {
    schedule: { type: 'minutes', interval: days * MINUTES_PER_DAY },
    description: `about ${days} day${days === 1 ? '' : 's'} apart`,
  };
}

/**
 * Derives a Schedule from the dates a task recurred on.
 *
 * Fewer than two dated occurrences carries no rhythm at all, so those degrade to a
 * one-off with `none` confidence rather than a fabricated interval.
 */
export function inferScheduleFromDates(dates: Date[]): ScheduleInference {
  const all = [...dates].sort((a, b) => a.getTime() - b.getTime());
  // Only the recent tail informs the estimate — see MAX_INFERENCE_OCCURRENCES.
  const sorted = all.slice(-MAX_INFERENCE_OCCURRENCES);
  const windowNote = all.length > sorted.length
    ? `most recent ${sorted.length} of ${all.length} occurrences`
    : `${sorted.length} occurrences`;

  if (sorted.length === 0) {
    return {
      schedule: { type: 'once', dueDate: toDateKey(new Date()) },
      confidence: 'none',
      medianGapDays: null,
      gapsInDays: [],
      rationale: 'No due or completion dates to infer from.',
    };
  }

  if (sorted.length === 1) {
    return {
      schedule: { type: 'once', dueDate: toDateKey(sorted[0]) },
      confidence: 'none',
      medianGapDays: null,
      gapsInDays: [],
      rationale: 'Only one dated occurrence — no interval to measure, treated as a one-off.',
    };
  }

  const gaps: number[] = [];
  for (let i = 1; i < sorted.length; i++) {
    gaps.push(daysBetween(sorted[i - 1], sorted[i]));
  }

  // Several instances sharing one date say nothing about spacing.
  const spacingGaps = gaps.filter((gap) => gap > 0);
  if (spacingGaps.length === 0) {
    return {
      schedule: { type: 'once', dueDate: toDateKey(sorted[sorted.length - 1]) },
      confidence: 'none',
      medianGapDays: null,
      gapsInDays: gaps,
      rationale: `${windowNote} all share the same date — no interval to measure.`,
    };
  }

  const medianGap = median(spacingGaps);
  const confidence = scoreConfidence(spacingGaps, medianGap);
  const { schedule, description } = scheduleForGap(medianGap, sorted);

  const spacingNote = spacingGaps.length === 1
    ? 'a single gap, so the rhythm could be coincidence'
    : `gaps of ${spacingGaps.join(', ')} days`;

  return {
    schedule,
    confidence,
    medianGapDays: medianGap,
    gapsInDays: gaps,
    rationale: `${windowNote} with ${spacingNote} — typically ${description}.`,
  };
}

/**
 * Normalised names of chores that already exist, for spotting candidates that would
 * duplicate one.
 *
 * Takes bare names rather than chore objects so the domain layer stays independent of
 * the aggregate shapes the store happens to hold.
 */
export function existingChoreTitles(choreNames: string[]): Set<string> {
  return new Set(choreNames.map(normalizeTitle));
}

/**
 * Whether importing this candidate would duplicate a chore that already exists.
 *
 * Title matching is a heuristic, not an identity check: it misses tasks renamed in
 * Google since the original import, and flags a chore that coincidentally shares a
 * name. It holds up because it reads live chore data, so it survives a reload and
 * also catches chores that were created by hand rather than imported.
 */
export function candidateAlreadyExists(
  candidate: ImportCandidate,
  existingTitles: Set<string>,
): boolean {
  return existingTitles.has(normalizeTitle(candidate.title));
}

/**
 * Turns raw task lists into reviewable import candidates, one per distinct title.
 *
 * Sorted by weight of evidence so the most confidently-inferred recurring chores —
 * the interesting ones — surface first.
 */
export function buildImportCandidates(data: TaskListWithTasks[]): ImportCandidate[] {
  const candidates: ImportCandidate[] = [];

  for (const { list, tasks } of data) {
    const groups = new Map<string, GoogleTask[]>();

    for (const task of tasks) {
      if (task.deleted) continue;
      const title = task.title?.trim();
      // Google keeps blank rows for tasks the user started and abandoned.
      if (!title) continue;

      const key = normalizeTitle(title);
      const existing = groups.get(key);
      if (existing) {
        existing.push(task);
      } else {
        groups.set(key, [task]);
      }
    }

    for (const [key, group] of groups) {
      const dates = group
        .map(occurrenceDate)
        .filter((date): date is Date => date !== null)
        .sort((a, b) => a.getTime() - b.getTime());

      const open = group.find((task) => task.status !== 'completed');

      candidates.push({
        key: `${list.id}:${key}`,
        listId: list.id,
        listTitle: list.title,
        title: (open ?? group[0]).title.trim(),
        notes: group.find((task) => task.notes)?.notes,
        occurrenceDates: dates,
        occurrenceCount: group.length,
        openTaskId: open?.id,
        hasOpenInstance: Boolean(open),
        inference: inferScheduleFromDates(dates),
      });
    }
  }

  const confidenceRank: Record<InferenceConfidence, number> = {
    high: 0, medium: 1, low: 2, none: 3,
  };

  return candidates.sort((a, b) => {
    const byConfidence = confidenceRank[a.inference.confidence] - confidenceRank[b.inference.confidence];
    if (byConfidence !== 0) return byConfidence;
    if (a.occurrenceCount !== b.occurrenceCount) return b.occurrenceCount - a.occurrenceCount;
    return a.title.localeCompare(b.title);
  });
}
